/**
 * The loyalty club's pure rules (release A, spec
 * docs/superpowers/specs/2026-10-24-loyalty-club-design.md).
 *
 * The card QR is DIGITS ONLY: `9917` + a 36-digit token. A wedge scanner types
 * keys, and under the Georgian keyboard layout Latin letters arrive as
 * Georgian ones; digits never change (spec §15). The poster QR is a URL that a
 * phone camera opens, so only the card needs the reserved form.
 *
 * phoneNorm is the twin of the database's private.phone_norm (188): change one,
 * change both.
 */

import type { Key } from './i18n'
import { parseLoyaltyPct } from './loyalty'

export const CARD_PREFIX = '9917'
export const CARD_DIGITS = 36

const CARD_RE = new RegExp(`^${CARD_PREFIX}(\\d{${CARD_DIGITS}})$`)

/** What a card page's QR carries. */
export function cardPayload(token: string): string {
  return CARD_PREFIX + token
}

/** Every till scan path asks this FIRST: a card never reaches the product lookup. */
export function parseTillScan(text: string): { kind: 'card'; token: string } | { kind: 'other'; text: string } {
  const m = CARD_RE.exec(text.trim())
  return m ? { kind: 'card', token: m[1] } : { kind: 'other', text }
}

export function isCardText(text: string): boolean {
  return CARD_RE.test(text.trim())
}

/** Digits only; drop a leading 995, then a leading 0. Georgian numbers end 9 digits; 7..12 kept, else none. */
export function phoneNorm(raw: string | null | undefined): string | null {
  let d = (raw ?? '').replace(/\D/g, '')
  if (d.startsWith('995')) d = d.slice(3)
  if (d.startsWith('0')) d = d.slice(1)
  return d.length >= 7 && d.length <= 12 ? d : null
}

export function emailNorm(raw: string | null | undefined): string | null {
  const e = (raw ?? '').trim().toLowerCase()
  return /^[^\s@]+@[^\s@]+$/.test(e) ? e : null
}

/** „5•• ••• 456" — what the till shows of a phone it asks to confirm. */
export function maskPhone(norm: string): string {
  return `${norm.slice(0, 1)}•• ••• ${norm.slice(-3)}`
}

/**
 * A self-typed phone is never a till lookup identity until a cashier confirms
 * it with the person in front of them (spec §6, Codex #I6). A customer staff
 * made — every ნისია customer before the club — is found by phone as today.
 */
export function phoneSearchable(c: { joined_via?: string | null; phone_confirmed_at?: string | null }): boolean {
  return c.joined_via !== 'self' || !!c.phone_confirmed_at
}

/** What the server answers of a customer's club standing (private.club_status). Display only. */
export type ClubStatus = { member: boolean; until: string | null; needed: number; month_sum: number }

/** A month before „-მდე" drops its ending: ნოემბერი → ნოემბრამდე, თებერვალი → თებერვლამდე, მარტი → მარტამდე. */
const KA_UNTIL = ['იანვრამდე', 'თებერვლამდე', 'მარტამდე', 'აპრილამდე', 'მაისამდე', 'ივნისამდე', 'ივლისამდე', 'აგვისტომდე',
  'სექტემბრამდე', 'ოქტომბრამდე', 'ნოემბრამდე', 'დეკემბრამდე']
const RU_GENITIVE = ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря']

/** The last day of membership (ClubStatus.until, 'YYYY-MM-DD') as „30 ნოემბრამდე" / „до 30 ноября". */
export function untilText(isoDate: string, lang: 'ka' | 'ru'): string {
  const [, m, d] = isoDate.split('-').map(Number)
  return lang === 'ka' ? `${d} ${KA_UNTIL[m - 1]}` : `до ${d} ${RU_GENITIVE[m - 1]}`
}

/**
 * The club's settings rows, read the same way at start and on the live refresh (Codex #I5): another device's
 * owner changing the threshold or the bonus reaches every till without a reload. A broken threshold is none —
 * the server's private.club_threshold says the same, so nobody is a member.
 */
export function clubSettingsFromKv(kv: Record<string, string | undefined>): {
  clubThreshold: number | null; clubCode: string; loyaltyEnabled: boolean; loyaltyPct: number
} {
  const thr = (kv['club.threshold'] ?? '').trim()
  return {
    clubThreshold: /^\d{1,7}(\.\d{1,2})?$/.test(thr) ? Number(thr) : null,
    clubCode: kv['club.code'] ?? '',
    loyaltyEnabled: kv.loyaltyEnabled === 'true',
    loyaltyPct: parseLoyaltyPct(kv.loyaltyPct ?? '') ?? 0,
  }
}

/**
 * A customer's club identity — only the club RPCs write it (188's identity guard refuses anyone else) and
 * email_norm / phone_norm are GENERATED (a write naming them fails). Every other customer write leaves them out:
 * the store's updateCustomer and the restore (lib/restore.ts).
 */
export const CLUB_CUSTOMER_COLUMNS = ['email', 'email_norm', 'email_verified_at', 'card_token', 'card_revoked_at',
  'phone_norm', 'phone_confirmed_at', 'joined_via', 'club_consent_at', 'club_rules_version', 'anonymized_at'] as const

export function withoutClubColumns<T extends Record<string, unknown>>(row: T): T {
  const out: Record<string, unknown> = { ...row }
  for (const c of CLUB_CUSTOMER_COLUMNS) delete out[c]
  return out as T
}

const CLUB_ERRORS = new Set(['CLUB_CODE_BAD', 'CLUB_OFF', 'CLUB_CODE_WRONG', 'CLUB_CODE_EXPIRED', 'CLUB_CARD_UNKNOWN',
  'CLUB_CARD_REVOKED', 'CLUB_NOT_THIS_SHOP', 'CLUB_NOT_MEMBER', 'CLUB_PHONE_TAKEN', 'CLUB_PHONE_BAD', 'CLUB_EMAIL_TAKEN',
  'CLUB_EMAIL_BAD', 'CLUB_ALREADY_MEMBER', 'CLUB_HAS_DEBT', 'CLUB_FORBIDDEN', 'CLUB_RATE', 'CLUB_NO_CARD', 'CLUB_FORM_BAD',
  'CLUB_OFFLINE', 'CUSTOMER_NOT_FOUND', 'club_unconfigured', 'club_turnstile'])

/** The words for a club refusal — the pages, the till and the owner's screens read the same table. */
export function clubErrorKey(code: unknown): Key {
  return (typeof code === 'string' && CLUB_ERRORS.has(code) ? `club.err.${code}` : 'club.err.generic') as Key
}

/** The product card's „კლუბის ფასი": empty = none; else to the tetri, above 0 and below retail (188's CHECK is
 *  only > 0, so a later retail cut never fails on the database — a club price at or above retail never wins). */
export function clubPriceOf(typed: string, retail: number): { value: number | null } | { error: Key } {
  const s = typed.trim()
  if (s === '') return { value: null }
  if (!/^\d{1,7}(\.\d{1,2})?$/.test(s)) return { error: 'club.product.bad' }
  const v = Number(s)
  return v > 0 && v < retail ? { value: v } : { error: 'club.product.bad' }
}

/**
 * The two numbers the club rules name ({pct}, {threshold}) as /api/club `info` answers them (Codex final #2): a
 * missing or broken term is NO rules text — the registration waits rather than ask consent to „0 %" or „₾0".
 * A threshold of 0 is a real choice (every card holder); a bonus of 0 % is not a club.
 */
export function clubTermsOf(j: { pct?: unknown; threshold?: unknown }): { pct: number; threshold: number } | null {
  const num = (v: unknown) => (v === null || v === undefined || v === '' ? NaN : Number(v))
  const pct = num(j.pct)
  const threshold = num(j.threshold)
  return Number.isFinite(pct) && pct > 0 && Number.isFinite(threshold) && threshold >= 0 ? { pct, threshold } : null
}

/**
 * What a product card's save sends of the club price (Codex final #5): nothing while the club is off or when the
 * owner left it as it was loaded — so a restock or any other edit never wipes a club price — else the new value
 * (empty = cleared) or the card's refusal.
 */
export function clubPricePatch(p: { clubOn: boolean; typed: string; loaded: number | null | undefined; retail: number }):
  { club_price?: number | null } | { error: Key } {
  if (!p.clubOn) return {}
  const typed = p.typed.trim()
  const loaded = p.loaded ?? null
  if (typed === '' && loaded === null) return {}
  if (typed !== '' && loaded !== null && Number(typed) === loaded) return {}
  const r = clubPriceOf(typed, p.retail)
  return 'error' in r ? r : { club_price: r.value }
}
